import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, ChatCircleText, LinkSimple, ShieldCheck, Globe, Check } from "@phosphor-icons/react";

const NAVY = "#0A0F2C";
const GOLD = "#C9A84C";

const FEATURES = [
  { Icon: ChatCircleText, title: "Ask in plain language",
    sub: "No SQL, no tickets, no training — just type your question and get answers instantly" },
  { Icon: LinkSimple, title: "Connects to every ERP",
    sub: "Oracle Fusion, SAP, Dynamics, NetSuite, Workday and more — one AI for all" },
  { Icon: ShieldCheck, title: "Enterprise-grade security",
    sub: "SOC 2 certified, UAE PDPL compliant — your data never leaves your instance" },
  { Icon: Globe, title: "Built for GCC & India",
    sub: "Arabic language support, AED/SAR/INR data natively — regional AI that understands your market" },
];

const STATS = [
  { num: "90%",  lbl: "Fewer IT tickets for finance reports" },
  { num: "4 hrs", lbl: "Saved per user per week on ERP tasks" },
  { num: "1 day", lbl: "To go live on your Oracle instance" },
];

const SYSTEMS = [
  { name: "Oracle Fusion", hl: true },
  { name: "SAP S/4HANA" }, { name: "MS Dynamics" }, { name: "NetSuite" },
  { name: "Workday" }, { name: "+ more" },
];

const inputStyle = {
  border: "1px solid #DDE1E9", backgroundColor: "#FAFAFA", color: "#0D1117",
};

export default function ForgotPasswordPage() {
  const nav = useNavigate();

  // step: "username" -> "otp" -> "reset"
  const [step, setStep] = useState("username");

  const [username, setUsername] = useState("");
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const otpRefs = useRef([]);

  // ── Step 1: validate username + admin check, then send OTP ────────
  const sendOtp = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      // TODO: confirm real endpoint/response shape with backend.
      // Assumed: POST /auth/forgot-password/check-username { username }
      //          -> { exists: boolean, is_admin: boolean }
      const { data } = await api.post("/auth/forgot-password/check-username", { username });

      if (!data?.exists) {
        setErr("We couldn't find an account with that username.");
        return;
      }
      if (!data?.is_admin) {
        setErr("You are not an admin. Please consult your admin.");
        return;
      }

      // TODO: trigger actual OTP send once check passes
      // await api.post("/auth/forgot-password/send-otp", { username });
      setStep("otp");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── OTP box handling ──────────────────────────────────────────────
  const handleOtpChange = (idx, value) => {
    const v = value.replace(/[^0-9a-zA-Z]/g, "").slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const text = e.clipboardData.getData("text").trim().slice(0, 6).split("");
    if (text.length) {
      e.preventDefault();
      setOtp((prev) => {
        const next = [...prev];
        text.forEach((ch, i) => { if (i < 6) next[i] = ch; });
        return next;
      });
      otpRefs.current[Math.min(text.length, 5)]?.focus();
    }
  };

  // ── Step 2: verify OTP (no validation yet, wire up later) ──────────
  const verifyOtp = (e) => {
    e.preventDefault();
    // TODO: await api.post("/auth/forgot-password/verify-otp", { username, otp: otp.join("") });
    setStep("reset");
  };

  // ── Step 3: save new password (no validation yet, wire up later) ───
  const savePassword = (e) => {
    e.preventDefault();
    // TODO: await api.post("/auth/forgot-password/reset", { username, otp: otp.join(""), new_password: newPassword });
    nav("/login");
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F0F2F5] py-10 px-4 font-sans">
      <div className="w-full max-w-[1080px] grid grid-cols-1 lg:grid-cols-[56%_44%] rounded-2xl overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.18)] bg-white"
        data-testid="forgot-password-shell">

        {/* ─────── LEFT (navy) — identical to LoginPage ─────── */}
        <aside className="hidden lg:flex flex-col gap-6 p-11" style={{ backgroundColor: NAVY }} data-testid="forgot-password-left">
          <div className="flex items-center gap-3">
            <div className="w-[56px] h-[56px] rounded-xl flex items-center justify-center text-[28px] font-semibold"
              style={{ backgroundColor: GOLD, color: NAVY }}>V</div>
            <div className="text-[26px] font-semibold text-white tracking-[1.5px]">
              VĀ<span style={{ color: GOLD }}>KU</span>
            </div>
          </div>

          <h1 className="text-[26px] font-semibold text-white leading-[1.35]">
            Your enterprise systems,<br />
            finally speaking <span style={{ color: GOLD }}>plain English.</span>
          </h1>

          <p className="text-[13.5px] leading-[1.75] max-w-[360px]" style={{ color: "#8B93B8" }}>
            VĀKU replaces complex ERP interfaces with a single AI layer — so your CFO, CIO, and every team member gets instant answers from Oracle, SAP, and every system they run.
          </p>

          <div className="flex flex-col gap-[13px]">
            {FEATURES.map(({ Icon, title, sub }) => (
              <div key={title} className="flex items-start gap-2.5">
                <div className="w-[30px] h-[30px] rounded-[7px] flex items-center justify-center shrink-0"
                  style={{ backgroundColor: "rgba(201,168,76,0.13)" }}>
                  <Icon size={16} weight="regular" style={{ color: GOLD }} />
                </div>
                <div>
                  <div className="text-[13px] font-semibold mb-0.5" style={{ color: "#E0E6FF" }}>{title}</div>
                  <div className="text-[11.5px] leading-[1.5]" style={{ color: "#8B93B8" }}>{sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2.5">
            {STATS.map((s) => (
              <div key={s.num} className="flex-1 rounded-[10px] px-3.5 py-3.5"
                style={{ backgroundColor: "rgba(255,255,255,0.055)", borderWidth: "0.5px",
                          borderStyle: "solid", borderColor: "rgba(255,255,255,0.1)" }}>
                <div className="text-[22px] font-semibold leading-none mb-1.5" style={{ color: GOLD }}>{s.num}</div>
                <div className="text-[10.5px] leading-[1.4]" style={{ color: "#8B93B8" }}>{s.lbl}</div>
              </div>
            ))}
          </div>

          <blockquote className="rounded-r-lg px-4 py-3.5" style={{ backgroundColor: "rgba(255,255,255,0.04)", borderLeft: `2.5px solid ${GOLD}` }}>
            <p className="text-[12.5px] italic leading-[1.65] mb-2" style={{ color: "#C8D0E8" }}>
              &ldquo;We used to raise 3 tickets a day just to get basic headcount reports. With VĀKU, our HR director gets answers in seconds — no IT involvement.&rdquo;
            </p>
            <div className="text-[11px]" style={{ color: "#8B93B8" }}>
              <strong style={{ color: GOLD, fontStyle: "normal" }}>CFO, UAE-based manufacturing group</strong> — Oracle Fusion customer
            </div>
          </blockquote>

          <div>
            <div className="text-[10px] uppercase tracking-[0.9px] mb-2" style={{ color: "#5A6180" }}>Connects to</div>
            <div className="flex flex-wrap gap-[7px]">
              {SYSTEMS.map((s) => (
                <span key={s.name} className="rounded-md px-2.5 py-1 text-[10.5px] font-medium"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.055)",
                    borderWidth: "0.5px", borderStyle: "solid",
                    borderColor: s.hl ? "rgba(201,168,76,0.35)" : "rgba(255,255,255,0.1)",
                    color: s.hl ? GOLD : "#8B93B8",
                  }}>
                  {s.name}
                </span>
              ))}
            </div>
          </div>
        </aside>

        {/* ─────── RIGHT (white) — forgot-password flow ─────── */}
        <section className="flex flex-col justify-center px-9 py-11 bg-white" data-testid="forgot-password-right">
          {/* Mobile-only logo */}
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="w-[56px] h-[56px] rounded-xl flex items-center justify-center text-[28px] font-semibold"
              style={{ backgroundColor: GOLD, color: NAVY }}>V</div>
            <div className="text-[26px] font-semibold tracking-[1.5px]" style={{ color: NAVY }}>
              VĀ<span style={{ color: GOLD }}>KU</span>
            </div>
          </div>

          {/* ── Step 1: Username ── */}
          {step === "username" && (
            <>
              <h2 className="text-[22px] font-semibold mb-1" style={{ color: "#0D1117" }} data-testid="forgot-password-title">
                Forgot your password?
              </h2>
              <p className="text-[13.5px] mb-6" style={{ color: "#666" }}>
                Enter your username and we&apos;ll send you a one-time code.
              </p>

              <form onSubmit={sendOtp} className="space-y-[15px]" data-testid="forgot-password-username-form">
                <div>
                  <label className="block text-[12.5px] font-medium mb-1.5" style={{ color: "#444" }}>Username</label>
                  <input
                    data-testid="forgot-password-username"
                    type="text" required
                    value={username} onChange={(e) => setUsername(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full h-10 rounded-lg px-3.5 text-[13.5px] outline-none transition-colors"
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = NAVY; e.currentTarget.style.backgroundColor = "#fff"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "#DDE1E9"; e.currentTarget.style.backgroundColor = "#FAFAFA"; }}
                  />
                </div>

                {err && <div className="text-[12px] font-semibold text-[#DC2626]" data-testid="forgot-password-error">{err}</div>}

                <button
                  data-testid="forgot-password-send-otp"
                  type="submit" disabled={loading}
                  className="w-full h-[42px] rounded-lg text-[14px] font-semibold tracking-[0.3px] text-white transition-colors disabled:opacity-60"
                  style={{ backgroundColor: NAVY }}
                  onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = "#141B40")}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = NAVY)}
                >
                  {loading ? "Checking…" : "Send OTP"}
                </button>
              </form>
            </>
          )}

          {/* ── Step 2: OTP ── */}
          {step === "otp" && (
            <>
              <h2 className="text-[22px] font-semibold mb-1" style={{ color: "#0D1117" }} data-testid="forgot-password-title">
                Enter verification code
              </h2>
              <p className="text-[13.5px] mb-6" style={{ color: "#666" }}>
                We sent a 6-digit code to the account for <strong>{username}</strong>.
              </p>

              <form onSubmit={verifyOtp} className="space-y-[15px]" data-testid="forgot-password-otp-form">
                <div className="flex gap-2.5 justify-between" data-testid="forgot-password-otp-boxes">
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (otpRefs.current[idx] = el)}
                      data-testid={`forgot-password-otp-${idx}`}
                      type="text"
                      inputMode="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      onPaste={handleOtpPaste}
                      className="w-11 h-12 text-center text-[18px] font-semibold rounded-lg outline-none transition-colors"
                      style={inputStyle}
                      onFocus={(e) => { e.currentTarget.style.borderColor = NAVY; e.currentTarget.style.backgroundColor = "#fff"; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = "#DDE1E9"; e.currentTarget.style.backgroundColor = "#FAFAFA"; }}
                    />
                  ))}
                </div>

                <button
                  data-testid="forgot-password-verify-otp"
                  type="submit"
                  className="w-full h-[42px] rounded-lg text-[14px] font-semibold tracking-[0.3px] text-white transition-colors"
                  style={{ backgroundColor: NAVY }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#141B40")}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = NAVY)}
                >
                  Verify
                </button>

                <button
                  type="button"
                  data-testid="forgot-password-resend-otp"
                  onClick={sendOtp}
                  className="w-full text-[12.5px] hover:underline"
                  style={{ color: "#888" }}
                >
                  Resend code
                </button>
              </form>
            </>
          )}

          {/* ── Step 3: New password ── */}
          {step === "reset" && (
            <>
              <h2 className="text-[22px] font-semibold mb-1" style={{ color: "#0D1117" }} data-testid="forgot-password-title">
                Set a new password
              </h2>
              <p className="text-[13.5px] mb-6" style={{ color: "#666" }}>
                Choose a strong password for your account.
              </p>

              <form onSubmit={savePassword} className="space-y-[15px]" data-testid="forgot-password-reset-form">
                <div>
                  <label className="block text-[12.5px] font-medium mb-1.5" style={{ color: "#444" }}>New password</label>
                  <input
                    data-testid="forgot-password-new-password"
                    type="password"
                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full h-10 rounded-lg px-3.5 text-[13.5px] outline-none transition-colors"
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = NAVY; e.currentTarget.style.backgroundColor = "#fff"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "#DDE1E9"; e.currentTarget.style.backgroundColor = "#FAFAFA"; }}
                  />
                </div>
                <div>
                  <label className="block text-[12.5px] font-medium mb-1.5" style={{ color: "#444" }}>Confirm password</label>
                  <input
                    data-testid="forgot-password-confirm-password"
                    type="password"
                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full h-10 rounded-lg px-3.5 text-[13.5px] outline-none transition-colors"
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = NAVY; e.currentTarget.style.backgroundColor = "#fff"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "#DDE1E9"; e.currentTarget.style.backgroundColor = "#FAFAFA"; }}
                  />
                </div>

                <button
                  data-testid="forgot-password-save"
                  type="submit"
                  className="w-full h-[42px] rounded-lg text-[14px] font-semibold tracking-[0.3px] text-white transition-colors inline-flex items-center justify-center gap-2"
                  style={{ backgroundColor: NAVY }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#141B40")}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = NAVY)}
                >
                  <Check size={15} weight="bold" /> Save password
                </button>
              </form>
            </>
          )}

          <Link to="/login" data-testid="forgot-password-back"
            className="inline-flex items-center gap-1.5 text-[12.5px] mt-6 hover:underline"
            style={{ color: "#888" }}>
            <ArrowLeft size={13} weight="bold" /> Back to sign in
          </Link>
        </section>
      </div>
    </div>
  );
}